import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List } from 'foundations-components/transitional/components/List';
import InvoiceVoidButton from './Buttons/InvoiceVoidButton';
import CopyLinkButton from './Buttons/CopyLinkButton';
import ViewInvoiceButton from './Buttons/ViewInvoiceButton';
import ViewPropertyHistoryButton from './Buttons/ViewPropertyHistoryButton';
import DownloadInvoiceButton from './Buttons/DownloadInvoiceButton';
import InvoiceDeleteButton from './Buttons/InvoiceDeleteButton';
import EditRemindersButton from './Buttons/EditRemindersButton';
import SendInvoiceButton from './Buttons/SendInvoiceButton';
import EditInvoiceButton from './Buttons/EditInvoiceButton';
import CloneInvoiceButton from './Buttons/CloneInvoiceButton';
import RecordPaymentButton from './Buttons/RecordPaymentButton';
import ChargePaymentMethodButton from './Buttons/ChargePaymentMethodButton';
import { filterAndFlattenActionGroups, insertDividers } from '../utils/addDropdownDividers';
import { InvoiceStatus } from '../constants/status';
import { INVOICE_PERMISSIONS_GATE } from '../constants/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const InvoiceActionsDropdown = props => {
  const {
    hasInvoiceEditPermissions,
    hasInvoiceDeletePermissions = hasInvoiceEditPermissions
  } = props.permissions;
  const {
    canEditOpenInvoice,
    canDelete,
    canEditReminders
  } = props.invoiceSettings;
  const {
    onClick,
    updatePreview,
    hasAllGates
  } = props.handlers;
  const isUngatedToInvoicePermissions = hasAllGates(INVOICE_PERMISSIONS_GATE);
  const {
    currencyValidatorLoading,
    currencyValidatorError,
    maybeCurrencyValidationError
  } = props.currencyValidator;
  const {
    clone: {
      cloneLoading,
      unknownCloneValidationError,
      cloneDisabledReasons
    },
    chargePaymentMethod: {
      onFileChargeValidationData,
      onFileChargeValidationLoading
    },
    recordPayment: {
      shouldDisableRecordPaymentButton
    }
  } = props.actions;
  const {
    isBillableInvoice,
    invoiceStatus,
    hasPayments,
    isAllAssociatedPaymentsFailed,
    hasUpcomingReminderDates,
    isPaymentCollectedAutomatically,
    hasAssociatedSubscription,
    associatedSubscriptionId,
    hasActiveSubscriptions,
    isBalancePositive,
    invoiceLink,
    invoicePropertyHistoryLink,
    hasAssociatedObjectToLink,
    invoiceId
  } = props.invoice;
  const isOpen = invoiceStatus === InvoiceStatus.OPEN;
  const isDraft = invoiceStatus === InvoiceStatus.DRAFT;
  const isFinalizedOrVoided = [InvoiceStatus.OPEN, InvoiceStatus.PAID, InvoiceStatus.VOIDED].includes(invoiceStatus);
  const disableEditButton = currencyValidatorLoading || currencyValidatorError || !!maybeCurrencyValidationError || hasPayments || isPaymentCollectedAutomatically || hasAssociatedSubscription && isOpen;
  const actionGroup1 = useMemo(() => {
    const BUTTON_CONFIGURATION = {
      EDIT: isBillableInvoice && (isDraft || isOpen && canEditOpenInvoice) && hasInvoiceEditPermissions,
      RECORD_PAYMENT: isBillableInvoice && isOpen && isBalancePositive && hasInvoiceEditPermissions
    };
    return [BUTTON_CONFIGURATION.EDIT && /*#__PURE__*/_jsx(EditInvoiceButton, {
      invoiceId: invoiceId,
      hasAllGates: hasAllGates,
      hasAssociatedSubscription: hasAssociatedSubscription,
      isPaymentCollectedAutomatically: isPaymentCollectedAutomatically,
      invoiceHasPayments: hasPayments,
      currencyValidatorError: currencyValidatorError,
      maybeCurrencyValidationError: maybeCurrencyValidationError,
      onClickButton: onClick,
      disabled: disableEditButton
    }, "editButton"), BUTTON_CONFIGURATION.RECORD_PAYMENT && /*#__PURE__*/_jsx(RecordPaymentButton, {
      onClickButton: onClick,
      isPaymentCollectedAutomatically: isPaymentCollectedAutomatically,
      hasAssociatedSubscription: hasAssociatedSubscription,
      associatedSubscriptionId: associatedSubscriptionId,
      updatePreview: updatePreview,
      disabled: shouldDisableRecordPaymentButton
    })];
  }, [isBillableInvoice, hasInvoiceEditPermissions, canEditOpenInvoice, currencyValidatorError, hasAssociatedSubscription, hasPayments, associatedSubscriptionId, isBalancePositive, isDraft, isOpen, isPaymentCollectedAutomatically, maybeCurrencyValidationError, disableEditButton, shouldDisableRecordPaymentButton, onClick, updatePreview, invoiceId, hasAllGates]);
  const actionGroup2 = useMemo(() => {
    const BUTTON_CONFIGURATION = {
      VIEW: isBillableInvoice && isFinalizedOrVoided,
      VIEW_PROPERTY_HISTORY: true
    };
    return [BUTTON_CONFIGURATION.VIEW && /*#__PURE__*/_jsx(ViewInvoiceButton, {
      invoiceLink: invoiceLink,
      onClickButton: onClick
    }, "viewButton"), BUTTON_CONFIGURATION.VIEW_PROPERTY_HISTORY && /*#__PURE__*/_jsx(ViewPropertyHistoryButton, {
      invoicePropertyHistoryLink: invoicePropertyHistoryLink,
      onClickButton: onClick
    }, "viewPropertyHistoryButton")];
  }, [isBillableInvoice, invoiceLink, invoicePropertyHistoryLink, isFinalizedOrVoided, onClick]);
  const actionGroup3 = useMemo(() => {
    const BUTTON_CONFIGURATION = {
      COPY: isBillableInvoice && isFinalizedOrVoided,
      SEND: isBillableInvoice && isFinalizedOrVoided,
      DOWNLOAD: isBillableInvoice && isFinalizedOrVoided,
      EDIT_REMINDERS: isBillableInvoice && isOpen && hasInvoiceEditPermissions && canEditReminders,
      CHARGE_PAYMENT_METHOD: isBillableInvoice && isOpen && isBalancePositive && hasInvoiceEditPermissions,
      CLONE: isBillableInvoice && hasInvoiceEditPermissions && !(isPaymentCollectedAutomatically && isDraft)
    };
    return [BUTTON_CONFIGURATION.COPY && /*#__PURE__*/_jsx(CopyLinkButton, {
      invoiceLink: invoiceLink,
      onClickButton: onClick
    }, "copyLinkButton"), BUTTON_CONFIGURATION.SEND && /*#__PURE__*/_jsx(SendInvoiceButton, {
      onClickButton: onClick,
      disabled: !hasAssociatedObjectToLink
    }, "sendButton"), BUTTON_CONFIGURATION.DOWNLOAD && /*#__PURE__*/_jsx(DownloadInvoiceButton, {
      onClickButton: onClick
    }, "downloadButton"), BUTTON_CONFIGURATION.EDIT_REMINDERS && /*#__PURE__*/_jsx(EditRemindersButton, {
      onClickButton: onClick,
      disabled: !hasUpcomingReminderDates
    }, "editRemindersButton"), BUTTON_CONFIGURATION.CHARGE_PAYMENT_METHOD && /*#__PURE__*/_jsx(ChargePaymentMethodButton, {
      disabled: !!onFileChargeValidationData || onFileChargeValidationLoading,
      onClickButton: onClick,
      canEditInvoice: canEditOpenInvoice && !disableEditButton,
      onFileValidationData: onFileChargeValidationData
    }), BUTTON_CONFIGURATION.CLONE && /*#__PURE__*/_jsx(CloneInvoiceButton, {
      disabledReasons: cloneDisabledReasons,
      disabled: cloneLoading || cloneDisabledReasons.length > 0 || unknownCloneValidationError,
      unknownValidationError: unknownCloneValidationError,
      onClickButton: onClick,
      invoiceId: invoiceId,
      hasAllGates: hasAllGates
    }, "cloneButton")];
  }, [isBillableInvoice, hasInvoiceEditPermissions, canEditReminders, cloneDisabledReasons, cloneLoading, hasAssociatedObjectToLink, hasUpcomingReminderDates, invoiceLink, isDraft, isFinalizedOrVoided, isOpen, isPaymentCollectedAutomatically, onFileChargeValidationData, onFileChargeValidationLoading, onClick, unknownCloneValidationError, canEditOpenInvoice, disableEditButton, isBalancePositive, invoiceId, hasAllGates]);
  const actionGroup4 = useMemo(() => {
    const BUTTON_CONFIGURATION = {
      DELETE: (isUngatedToInvoicePermissions ? hasInvoiceDeletePermissions : hasInvoiceEditPermissions) && (!isBillableInvoice || canDelete || isDraft),
      VOID: isBillableInvoice && isOpen && hasInvoiceEditPermissions
    };
    return [BUTTON_CONFIGURATION.DELETE && /*#__PURE__*/_jsx(InvoiceDeleteButton, {
      disabled: isBillableInvoice && (isPaymentCollectedAutomatically || hasActiveSubscriptions || hasPayments),
      hasPayments: hasPayments,
      onClickButton: onClick
    }, "deleteButton"), BUTTON_CONFIGURATION.VOID && /*#__PURE__*/_jsx(InvoiceVoidButton, {
      disabled: hasPayments && !isAllAssociatedPaymentsFailed,
      onClickButton: onClick
    }, "voidButton")];
  }, [isBillableInvoice, canDelete, isUngatedToInvoicePermissions, hasInvoiceEditPermissions, hasInvoiceDeletePermissions, hasActiveSubscriptions, hasPayments, isAllAssociatedPaymentsFailed, isDraft, isOpen, isPaymentCollectedAutomatically, onClick]);
  const availableActionsWithDividers = useMemo(() => insertDividers(filterAndFlattenActionGroups([actionGroup1, actionGroup2, actionGroup3, actionGroup4])), [actionGroup1, actionGroup2, actionGroup3, actionGroup4]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(Dropdown, {
      buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.components.invoiceActionsDropdown.actionsButton"
      }),
      menuWidth: props.menuWidth,
      buttonUse: props.buttonUse,
      placement: props.placement,
      buttonSize: props.buttonSize,
      "data-test-id": props['data-test-id'],
      children: /*#__PURE__*/_jsx(List, {
        children: availableActionsWithDividers
      })
    })
  });
};
export default InvoiceActionsDropdown;